import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import Header from '../components/Header';

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { monthSummaries, isConnected } = useApp();

  const handleContinue = () => {
    navigate('/mmt/categorize');
  };

  const handleConnectAccounts = () => {
    navigate('/mmt/landing');
  };

  // Empty state if no accounts connected
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-secondary">
        <Header />
        <div className="flex flex-col items-center justify-center p-6 min-h-[calc(100vh-80px)]">
          <div className="max-w-2xl text-center bg-white rounded-3xl p-12 shadow-xl">
            <h1 className="text-5xl font-bold mb-6">Welcome to Money Mindset Tool!</h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              To get started, connect your bank accounts to see your spending patterns
              and gain insights into your money mindset.
            </p>
            <button
              onClick={handleConnectAccounts}
              className="bg-primary text-white px-12 py-4 rounded-full text-xl font-semibold hover:bg-primary/90 transition-all transform hover:scale-105 shadow-lg"
            >
              Connect Your Accounts
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Header />
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold text-center mb-4">Overview</h1>

          <p className="text-center text-lg text-gray-700 mb-12 max-w-3xl mx-auto">
            Thank you for connecting your accounts. Below is your spending overview for the
            <span className="font-semibold text-primary"> last 3 calendar months</span>. This is just the beginning—categorize your transactions to get deeper insights into your money mindset!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {monthSummaries.map((summary, index) => (
              <div
                key={summary.month}
                className="bg-white rounded-2xl p-8 shadow-xl border-t-4 border-primary hover:shadow-2xl transition-shadow"
              >
                {/* Month Header with subtle badge */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-800">
                    {summary.month}
                  </h2>
                  <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                    {index === 0 ? 'Latest' : `${index + 1} mo ago`}
                  </span>
                </div>

                {/* Transaction Count */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold mb-1">
                    Transactions
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {summary.transactionCount}
                  </p>
                </div>

                {/* Money Flow */}
                <div className="space-y-4">
                  {/* Money In */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600">Money In</span>
                    <span className="text-lg font-bold text-green-600">
                      ${summary.moneyIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Money Out */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600">Money Out</span>
                    <span className="text-lg font-bold text-red-600">
                      ${summary.moneyOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Net Change - Highlighted */}
                  <div className={`mt-4 pt-4 border-t-2 flex justify-between items-center ${
                    summary.netChange >= 0 ? 'border-green-200' : 'border-red-200'
                  }`}>
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Net</span>
                    <span className={`text-2xl font-bold ${
                      summary.netChange >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {summary.netChange >= 0 ? '+' : ''}${Math.abs(summary.netChange).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={handleContinue}
              className="bg-primary text-white px-12 py-4 rounded-full text-xl font-semibold hover:bg-primary/90 transition-all transform hover:scale-105 shadow-lg"
            >
              Categorize Transactions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
